// Dashboard.jsx
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import "./styles/Dashboard.css";

export default function Dashboard() {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [attendanceData, setAttendanceData] = useState([]);
    const [staffData, setStaffData] = useState([]);
    const [activeStaffCount, setActiveStaffCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    
    // Modal states
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('add');
    const [selectedStaff, setSelectedStaff] = useState(null);
    const [formData, setFormData] = useState({
        names: '',
        phonenumber: '',
        password: '',
        role: 'staff'
    });
    const [submitting, setSubmitting] = useState(false);
    const [formError, setFormError] = useState('');

    const token = localStorage.getItem("token");
    const backendUrl = "https://sokonishushi.co.tz/api";
    const dropdownRef = useRef(null);

    useEffect(() => {
        const userData = {
            names: localStorage.getItem("names") || localStorage.getItem("name"),
            role: localStorage.getItem("role"),
            staff_id: localStorage.getItem("staff_id"),
            phonenumber: localStorage.getItem("phonenumber")
        };
        setUser(userData);
        fetchAttendanceData();
        fetchStaffData();
        
        const handleResize = () => {
            if (window.innerWidth > 768 && sidebarOpen) {
                setSidebarOpen(false);
            }
        };
        
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setDropdownOpen(false);
            }
        };
        
        window.addEventListener('resize', handleResize);
        document.addEventListener('mousedown', handleClickOutside);
        
        return () => {
            window.removeEventListener('resize', handleResize);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const fetchAttendanceData = async () => {
        try {
            const attendanceRes = await fetch(`${backendUrl}/attendance.php`, {
                headers: { "Authorization": `Bearer ${token}` }
            });
            const data = await attendanceRes.json();
            if (data.success) {
                setAttendanceData(data.attendance);
                
                const today = new Date().toISOString().split('T')[0];
                const signedInToday = data.attendance.filter(record => 
                    record.sign_date === today && record.action === 'sign_in'
                );
                const signedOutToday = data.attendance.filter(record => 
                    record.sign_date === today && record.action === 'sign_out'
                );
                
                const signedInIds = new Set(signedInToday.map(r => r.staff_id));
                const signedOutIds = new Set(signedOutToday.map(r => r.staff_id));
                
                let activeCount = 0;
                signedInIds.forEach(id => {
                    if (!signedOutIds.has(id)) activeCount++;
                });
                setActiveStaffCount(activeCount);
            }
        } catch (error) {
            console.error("Error:", error);
        }
    };

    const fetchStaffData = async () => {
        try {
            const response = await fetch(`${backendUrl}/get_staff.php`, {
                headers: { "Authorization": `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                setStaffData(data.staff);
            }
        } catch (error) {
            console.error("Error fetching staff:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        localStorage.clear();
        navigate("/");
        window.location.reload(); // Force reload to clear any cached state
    };

    const getTotalAttendance = () => {
        return attendanceData.length;
    };

    const getTodayAttendance = () => {
        const today = new Date().toISOString().split('T')[0];
        return attendanceData.filter(a => a.sign_date === today).length;
    };

    const openAddModal = () => {
        setModalMode('add');
        setSelectedStaff(null);
        setFormData({
            names: '',
            phonenumber: '',
            password: '',
            role: 'staff'
        });
        setFormError('');
        setIsModalOpen(true);
    };

    const openEditModal = (staff) => {
        setModalMode('edit');
        setSelectedStaff(staff);
        setFormData({
            names: staff.names,
            phonenumber: staff.phonenumber,
            password: '',
            role: staff.role
        });
        setFormError('');
        setIsModalOpen(true);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        setFormError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.names.trim()) {
            setFormError('Names are required');
            return;
        }
        if (!formData.phonenumber.trim()) {
            setFormError('Phone number is required');
            return;
        }
        if (modalMode === 'add' && !formData.password) {
            setFormError('Password is required for new staff');
            return;
        }
        if (modalMode === 'add' && (formData.password.length !== 4 || !/^\d+$/.test(formData.password))) {
            setFormError('Password must be 4 digits');
            return;
        }
        
        setSubmitting(true);
        
        try {
            let url = '';
            let method = '';
            let body = {};
            
            if (modalMode === 'add') {
                url = `${backendUrl}/create_staff.php`;
                method = 'POST';
                body = {
                    names: formData.names,
                    phonenumber: formData.phonenumber,
                    password: formData.password,
                    role: formData.role
                };
            } else {
                url = `${backendUrl}/update_staff.php`;
                method = 'POST';
                body = {
                    id: selectedStaff.id,
                    names: formData.names,
                    phonenumber: formData.phonenumber,
                    role: formData.role
                };
                if (formData.password) {
                    body.password = formData.password;
                }
            }
            
            const response = await fetch(url, {
                method: method,
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(body)
            });
            
            const data = await response.json();
            
            if (data.success || data.success === true) {
                setIsModalOpen(false);
                fetchStaffData();
            } else {
                setFormError(data.error || 'Operation failed');
            }
        } catch (error) {
            console.error('Error:', error);
            setFormError('Network error. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (staff) => {
        if (!window.confirm(`Are you sure you want to delete ${staff.names}?`)) {
            return;
        }
        
        try {
            const response = await fetch(`${backendUrl}/delete_staff.php`, {
                method: 'POST',
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({ id: staff.id })
            });
            
            const data = await response.json();
            
            if (data.success) {
                fetchStaffData();
            } else {
                alert('Failed to delete staff member');
            }
        } catch (error) {
            console.error('Error:', error);
            alert('Network error. Please try again.');
        }
    };

    const toggleSidebar = () => {
        setSidebarOpen(!sidebarOpen);
    };

    const toggleDropdown = () => {
        setDropdownOpen(!dropdownOpen);
    };

    const getUserInitials = () => {
        if (!user?.names) return 'A';
        const names = user.names.split(' ');
        if (names.length >= 2) {
            return (names[0].charAt(0) + names[1].charAt(0)).toUpperCase();
        }
        return user.names.charAt(0).toUpperCase();
    };

    if (loading) {
        return (
            <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Loading dashboard...</p>
            </div>
        );
    }

    return (
        <div className="dashboard">
            {/* Mobile Menu Button */}
            <button className="mobile-menu-btn" onClick={toggleSidebar}>
                <span className="menu-icon">☰</span>
            </button>
            
            {/* User Profile Dropdown - Top Right */}
            <div className="user-dropdown-container" ref={dropdownRef}>
                <div className="user-avatar" onClick={toggleDropdown}>
                    <div className="avatar-circle">
                        {getUserInitials()}
                    </div>
                    <div className="user-info-text">
                        <span className="user-name-display">{user?.names?.split(" ")[0] || 'Admin'}</span>
                        <span className="user-role-badge">{user?.role || 'admin'}</span>
                    </div>
                    <svg className={`dropdown-arrow ${dropdownOpen ? 'open' : ''}`} viewBox="0 0 24 24" width="16" height="16">
                        <path d="M7 10l5 5 5-5z" fill="currentColor"/>
                    </svg>
                </div>
                
                {dropdownOpen && (
                    <div className="dropdown-menu">
                        <div className="dropdown-user-info">
                            <div className="dropdown-avatar-large">
                                {getUserInitials()}
                            </div>
                            <div className="dropdown-user-details">
                                <div className="dropdown-user-name">{user?.names || 'Admin'}</div>
                                <div className="dropdown-user-role">{user?.role || 'admin'}</div>
                                <div className="dropdown-user-phone">{user?.phonenumber || 'N/A'}</div>
                            </div>
                        </div>
                        <div className="dropdown-menu-items">
                            <button onClick={handleLogout} className="dropdown-logout">
                                <svg className="dropdown-icon" viewBox="0 0 24 24" width="18" height="18">
                                    <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z" fill="currentColor"/>
                                </svg>
                                Logout
                            </button>
                        </div>
                    </div>
                )}
            </div>
            
            {/* Sidebar */}
            <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
                <div className="sidebar-header">
                    <button className="mobile-close-btn" onClick={toggleSidebar}>×</button>
                    <h2>Attendance System</h2>
                </div>

                <nav className="sidebar-nav">
                    <Link to="/dashboard" className="nav-link active" onClick={() => setSidebarOpen(false)}>
                        <span className="nav-icon">📊</span>
                        <span>Overview</span>
                    </Link>
                    <Link to="/admin-attendance" className="nav-link" onClick={() => setSidebarOpen(false)}>
                        <span className="nav-icon">📍</span>
                        <span>Attendance Records</span>
                    </Link>
                    <Link to="/schedule" className="nav-link" onClick={() => setSidebarOpen(false)}>
                        <span className="nav-icon">📅</span>
                        <span>Schedule</span>
                    </Link>
                    <Link to="/reports" className="nav-link" onClick={() => setSidebarOpen(false)}>
                        <span className="nav-icon">📄</span>
                        <span>Reports</span>
                    </Link>
                </nav>
            </aside>
            
            {/* Overlay for mobile sidebar */}
            {sidebarOpen && <div className="sidebar-overlay" onClick={toggleSidebar}></div>}

            {/* Main Content */}
            <main className="main-content">
                <div className="welcome-section">
                    <h1>Welcome back, {user?.names?.split(" ")[0] || "Admin"}!</h1>
                    <p>{new Date().toLocaleDateString('en-US', { 
                        weekday: 'long', 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                    })}</p>
                </div>

                {/* Stats Cards */}
                <div className="stats-grid">
                    <div className="stat-card">
                        <div className="stat-number">{getTotalAttendance()}</div>
                        <div className="stat-label">Total Attendance</div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-number">{getTodayAttendance()}</div>
                        <div className="stat-label">Today's Attendance</div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-number">{activeStaffCount}</div>
                        <div className="stat-label">Active Staff (Signed In)</div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-number">{staffData.length}</div>
                        <div className="stat-label">Total Staff</div>
                    </div>
                </div>

                {/* Staff Management Section */}
                <div className="staff-section">
                    <div className="section-header">
                        <h2>All Staff Members</h2>
                    </div>
                    <div className="table-container">
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>Names</th>
                                    <th>Phone Number</th>
                                    <th>Role</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {staffData.length > 0 ? (
                                    staffData.map((staff) => (
                                        <tr key={staff.id}>
                                            <td>{staff.names}</td>
                                            <td>{staff.phonenumber}</td>
                                            <td>
                                                <span className={`role-badge ${staff.role}`}>
                                                    {staff.role}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="action-buttons">
                                                    <button 
                                                        className="edit-btn"
                                                        onClick={() => openEditModal(staff)}
                                                    >
                                                        Edit
                                                    </button>
                                                    <button 
                                                        className="delete-btn"
                                                        onClick={() => handleDelete(staff)}
                                                    >
                                                        Delete
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="4" className="empty-message">No staff found</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </main>

            {/* Floating Action Button */}
            <button className="fab" onClick={openAddModal}>
                +
            </button>

            {/* Modal for Add/Edit Staff */}
            {isModalOpen && (
                <div className="modal-overlay" onClick={() => setIsModalOpen(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{modalMode === 'add' ? 'Add New Staff' : 'Edit Staff'}</h2>
                            <button className="close-btn" onClick={() => setIsModalOpen(false)}>×</button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="form-group">
                                <label>Full Names *</label>
                                <input
                                    type="text"
                                    name="names"
                                    value={formData.names}
                                    onChange={handleInputChange}
                                    placeholder="Enter full name"
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Phone Number *</label>
                                <input
                                    type="tel"
                                    name="phonenumber"
                                    value={formData.phonenumber}
                                    onChange={handleInputChange}
                                    placeholder="Enter phone number"
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Password {modalMode === 'edit' && '(Leave blank to keep current)'}</label>
                                <input
                                    type="password"
                                    name="password"
                                    value={formData.password}
                                    onChange={handleInputChange}
                                    placeholder={modalMode === 'add' ? '4-digit password' : 'New password (optional)'}
                                    maxLength="4"
                                    pattern="\d*"
                                />
                                <small>Must be 4 digits</small>
                            </div>
                            <div className="form-group">
                                <label>Role</label>
                                <select name="role" value={formData.role} onChange={handleInputChange}>
                                    <option value="staff">Staff</option>
                                    <option value="admin">Admin</option>
                                </select>
                            </div>
                            {formError && <div className="form-error">{formError}</div>}
                            <div className="form-buttons">
                                <button type="button" className="cancel-btn" onClick={() => setIsModalOpen(false)}>
                                    Cancel
                                </button>
                                <button type="submit" className="submit-btn" disabled={submitting}>
                                    {submitting ? 'Saving...' : (modalMode === 'add' ? 'Add Staff' : 'Save Changes')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}